import { computed, ref } from 'vue'
import { useTemplateStore } from '@/stores/template'

/**
 * Composable para gerenciar permissões de usuário
 * Fornece métodos para verificar permissões e carregar permissões do usuário
 */
export function usePermissions() {
  const store = useTemplateStore()
  const isLoadingPermissions = ref(false)
  const lastPermissionsUpdate = ref(null)

  /**
   * Verifica se o usuário tem uma permissão específica
   * @param {string} permission - Permissão a ser verificada
   * @returns {boolean} Verdadeiro se o usuário tem a permissão
   */
  const hasPermission = permission => {
    if (!store.permissions || !Array.isArray(store.permissions)) {
      return false
    }

    return store.permissions.includes(permission)
  }

  /**
   * Verifica se o usuário tem acesso a um site específico
   * @param {string} site - Nome do site a ser verificado
   * @returns {boolean} Verdadeiro se o usuário tem acesso ao site
   */
  const hasSiteAccess = site => {
    return hasPermission(`site:${site}`)
  }

  /**
   * Verifica se o usuário tem acesso a um módulo específico
   * @param {string} module - Nome do módulo a ser verificado
   * @returns {boolean} Verdadeiro se o usuário tem acesso ao módulo
   */
  const hasModuleAccess = module => {
    return hasPermission(`module:${module}`)
  }

  /**
   * Lista de módulos habilitados para o usuário
   */
  const enabledModules = computed(() => {
    if (!store.permissions || !Array.isArray(store.permissions)) {
      return []
    }

    return store.permissions
      .filter(permission => permission.startsWith('module:'))
      .map(permission => permission.replace('module:', ''))
  })

  /**
   * Lista de sites aos quais o usuário tem acesso
   */
  const accessibleSites = computed(() => {
    if (!store.permissions || !Array.isArray(store.permissions)) {
      return []
    }

    return store.permissions
      .filter(permission => permission.startsWith('site:'))
      .map(permission => permission.replace('site:', ''))
  })

  /**
   * Verifica se o usuário é administrador
   */
  const isAdmin = computed(() => {
    return hasPermission('overwatch:admin')
  })

  /**
   * Verifica se as permissões foram carregadas recentemente
   * @param {number} maxAgeMs - Tempo máximo em milissegundos desde a última atualização (padrão: 15 minutos)
   * @returns {boolean} Verdadeiro se as permissões foram atualizadas recentemente
   */
  const hasRecentPermissions = (maxAgeMs = 15 * 60 * 1000) => {
    if (!lastPermissionsUpdate.value) return false

    const age = Date.now() - lastPermissionsUpdate.value
    return age < maxAgeMs
  }

  /**
   * Carrega as permissões do usuário do servidor
   * @param {boolean} force - Se verdadeiro, força o recarregamento mesmo que já tenha carregado recentemente
   * @returns {Promise<Array>} Promessa que resolve para a lista de permissões
   */
  const loadUserPermissions = async (force = false) => {
    // Se já estiver carregando, aguarda a conclusão
    if (isLoadingPermissions.value) {
      return store.permissions
    }

    // Se as permissões já foram carregadas recentemente e não é uma carga forçada, retorna as permissões atuais
    if (!force && hasRecentPermissions()) {
      return store.permissions
    }

    try {
      isLoadingPermissions.value = true

      // Se está sendo forçado, limpa o cache do token para garantir dados atualizados
      if (force) {
        store.clearTokenCache()
        console.log(
          'Cache de token limpo devido à atualização forçada de permissões'
        )
      }

      const permissions = await store.fetchUserPermissions()
      lastPermissionsUpdate.value = Date.now()
      return permissions
    } catch (error) {
      console.error('Error loading user permissions:', error)
      throw error
    } finally {
      isLoadingPermissions.value = false
    }
  }

  /**
   * Recarrega as permissões do usuário e executa um callback quando concluído
   * @param {Function} callback - Função a ser executada após o carregamento das permissões
   */
  const ensurePermissions = async callback => {
    try {
      await loadUserPermissions()
      if (callback && typeof callback === 'function') {
        callback(store.permissions)
      }
    } catch (error) {
      console.error('Error ensuring permissions:', error)
    }
  }

  return {
    hasPermission,
    hasSiteAccess,
    hasModuleAccess,
    enabledModules,
    accessibleSites,
    isAdmin,
    loadUserPermissions,
    ensurePermissions,
    isLoadingPermissions,
    hasRecentPermissions
  }
}
